import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  Button,
  Checkbox,
  Input,
  Popconfirm,
  Segmented,
  Select,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useList } from '@refinedev/core';
import type {
  CompanyPromptGridRow,
  CoreVersion,
  PromptPipeline,
  PromptTemplate,
} from '@viq-admin/types';
import {
  PROMPT_PIPELINES,
  PIPELINE_LABELS,
  listCompanyGrid,
  listCoreVersions,
  rolloutCore,
  applyTemplate,
} from '../../api/promptStore';
import { Can } from '../../ability/AbilityContext';
import { formatDateTime, space } from '@viq-admin/ui';

/**
 * AI Prompt Rollout — per-pipeline grid of every company's pinned core
 * version + append-template state, with bulk actions to roll a set of
 * selected companies forward/back to a specific core version, or apply an
 * append template to them. A plain AntD `Table` (NOT `DataTable`/
 * `RowActionsMenu` — multi-row bulk actions don't fit that shared per-row
 * component), so this page is NOT routed through `consoleCapabilities.ts`;
 * gating is CASL `<Can>` direct, same as `CoreVersionsDrawer`.
 *
 * Deferred: bulk "clear append" is NOT built here — `CompanyPromptGridRow`
 * carries `sourceTemplateName` but not `sourceTemplateId`, so `detachTemplate`
 * can't be called from a grid row. Single-company clear lives in Task 7
 * (Company detail, which has `sourceTemplateId`); a bulk clear-append is a
 * separate backlog ticket.
 *
 * Fetch pattern mirrors `AppliedCompaniesModal`/`CoreVersionsDrawer`: bare
 * useEffect + useState + `message.error` — the custom
 * `/v1/superadmin/core-prompts*` endpoints have no corresponding Refine
 * resource. The append-template picker DOES have a Refine resource
 * (`prompt-templates`, already global/scope-exempt — see dataProvider.ts's
 * SCOPE_EXEMPT_RESOURCES), so that one uses `useList` and filters
 * client-side to the active pipeline.
 *
 * The row-selection checkbox column hides the header "select all" checkbox
 * (`hideSelectAll`) — both a safety choice (no single click that rolls out a
 * core-version change to every company in the grid at once) and load-bearing
 * for this file's own test: with the header checkbox hidden, the ONLY
 * checkbox rendered before the first data row is the "Behind only" filter
 * (below), so `getAllByRole('checkbox')[1]` deterministically lands on the
 * first body row (see list.test.tsx).
 *
 * The "behind active" filter checkbox is labelled "Behind only" — matching
 * `CompanyPromptGridRow.isBehindActive` and the "⚠ behind" row `Tag` below
 * (consistent product terminology across this feature). That means two
 * elements now carry the substring "behind": the row `Tag` and this
 * checkbox's own label. The test disambiguates them by asserting the row
 * Tag's exact text, `screen.getByText('⚠ behind')`, rather than a loose
 * `/behind/i` regex — Testing Library reads an AntD Checkbox's label span as
 * its own direct-text node, so a substring match would hit both and throw on
 * "multiple elements."
 *
 * The `/prompt-rollout` ROUTE is gated `read CorePrompt` (App.tsx) — the same
 * shape as the sibling `/core-prompts` route: a `super_admin_readonly` user
 * can reach this page and see the read-only grid, but the bulk-action bar
 * (this component's own `<Can I="manage" a="CorePrompt">`, below) stays
 * hidden for them.
 */
export function PromptRolloutList() {
  const [pipeline, setPipeline] = useState<PromptPipeline>(PROMPT_PIPELINES[0]);
  const [rows, setRows] = useState<CompanyPromptGridRow[]>([]);
  const [versions, setVersions] = useState<CoreVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<CompanyPromptGridRow[]>([]);

  // Filters — applied client-side over the fetched `rows`.
  const [versionFilter, setVersionFilter] = useState<number | null>(null);
  const [behindOnly, setBehindOnly] = useState(false);
  const [nameFilter, setNameFilter] = useState('');

  // Bulk action: set core version (also covers rollback — pick an older version).
  const [targetVersionId, setTargetVersionId] = useState<string | null>(null);
  const [settingVersion, setSettingVersion] = useState(false);

  // Bulk action: apply append template.
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  const selectedIds = selectedRows.map((r) => r.companyId);

  const { result: templatesResult, query: templatesQuery } = useList<PromptTemplate>({
    resource: 'prompt-templates',
  });
  const templateOptions = useMemo(
    () =>
      (templatesResult?.data ?? [])
        .filter((t) => t.pipeline === pipeline)
        .map((t) => ({ label: t.name, value: t.id })),
    [templatesResult, pipeline],
  );

  function refresh() {
    setLoading(true);
    return Promise.all([listCompanyGrid(pipeline), listCoreVersions(pipeline)])
      .then(([gridRows, coreVersions]) => {
        setRows(gridRows);
        setVersions(coreVersions);
      })
      .catch((err) =>
        message.error((err as { message?: string }).message ?? 'Failed to load rollout data'),
      )
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    // A pipeline switch invalidates any in-flight bulk-action selection —
    // company ids and version/template choices from the previous pipeline
    // don't carry over.
    setSelectedRows([]);
    setTargetVersionId(null);
    setTemplateId(null);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipeline]);

  const filteredRows = useMemo(
    () =>
      rows.filter((r) => {
        if (versionFilter != null && r.pinnedVersion !== versionFilter) return false;
        if (behindOnly && !r.isBehindActive) return false;
        if (nameFilter && !r.name.toLowerCase().includes(nameFilter.toLowerCase())) return false;
        return true;
      }),
    [rows, versionFilter, behindOnly, nameFilter],
  );

  // Selection survives filtering (preserveSelectedRowKeys), so a bulk action can
  // reach companies the operator can't currently see — say so rather than let
  // the count silently disagree with the table.
  const hiddenSelectedCount = useMemo(() => {
    const visible = new Set(filteredRows.map((r) => r.companyId));
    return selectedRows.filter((r) => !visible.has(r.companyId)).length;
  }, [filteredRows, selectedRows]);

  /** Selected companies that already carry an append — apply-template replaces it. */
  const overwriteCount = selectedRows.filter((r) => r.hasAppend).length;

  async function handleSetVersion() {
    if (!targetVersionId || selectedIds.length === 0) return;
    setSettingVersion(true);
    try {
      const count = await rolloutCore(pipeline, targetVersionId, selectedIds);
      message.success(`Set core version for ${count} compan${count === 1 ? 'y' : 'ies'}`);
      setSelectedRows([]);
      setTargetVersionId(null);
      await refresh();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Failed to set core version');
    } finally {
      setSettingVersion(false);
    }
  }

  async function handleApplyTemplate() {
    if (!templateId || selectedIds.length === 0) return;
    setApplyingTemplate(true);
    try {
      const count = await applyTemplate(templateId, selectedIds);
      message.success(`Applied template to ${count} compan${count === 1 ? 'y' : 'ies'}`);
      setSelectedRows([]);
      setTemplateId(null);
      await refresh();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Failed to apply template');
    } finally {
      setApplyingTemplate(false);
    }
  }

  const columns: ColumnsType<CompanyPromptGridRow> = [
    { dataIndex: 'name', key: 'name', title: 'Company' },
    {
      dataIndex: 'pinnedVersion',
      key: 'pinnedVersion',
      title: 'Pinned version',
      render: (v: number, record: CompanyPromptGridRow) => (
        <>
          {v}
          {record.isBehindActive && (
            <Tag color="red" style={{ marginLeft: space.sm }}>
              ⚠ behind
            </Tag>
          )}
        </>
      ),
    },
    {
      key: 'customRules',
      title: 'Custom rules',
      // An append whose source template was deleted has hasAppend=true but a null
      // template name; rendering that raw shows a blank cell, which reads as "no
      // custom rules". Name the state instead.
      render: (_, record) =>
        record.hasAppend ? (record.sourceTemplateName ?? 'custom (source deleted)') : '—',
    },
    {
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      title: 'Updated',
      render: (v: string) => formatDateTime(v),
    },
  ];

  return (
    <>
      <Typography.Title level={3}>AI Prompt Rollout</Typography.Title>
      <Typography.Paragraph type="secondary">
        Set version repins the selected companies — this is what actually delivers a prompt change.
        Apply template replaces their custom rules. <Link to="/prompt-guide">How this works</Link>
      </Typography.Paragraph>
      {/* Scrolls sideways on a phone rather than widening the page. */}
      <div style={{ overflowX: 'auto', marginBottom: space.lg }}>
        <Segmented
          value={pipeline}
          onChange={(v) => setPipeline(v as PromptPipeline)}
          options={PROMPT_PIPELINES.map((p) => ({ label: PIPELINE_LABELS[p], value: p }))}
        />
      </div>

      <div
        style={{
          display: 'flex',
          gap: space.md,
          alignItems: 'center',
          marginBottom: space.md,
          flexWrap: 'wrap',
        }}
      >
        <Select
          placeholder="Filter by version"
          allowClear
          style={{ width: 180 }}
          value={versionFilter ?? undefined}
          onChange={(v) => setVersionFilter(v ?? null)}
          options={versions.map((v) => ({ label: `Version ${v.version}`, value: v.version }))}
        />
        <Checkbox checked={behindOnly} onChange={(e) => setBehindOnly(e.target.checked)}>
          Behind only
        </Checkbox>
        <Input
          placeholder="Search by company name"
          allowClear
          style={{ width: 240 }}
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
        />
      </div>

      <Can I="manage" a="CorePrompt" passThrough>
        {(allowed: boolean) =>
          allowed ? (
            <div
              style={{
                display: 'flex',
                gap: space.md,
                alignItems: 'center',
                marginBottom: space.md,
                flexWrap: 'wrap',
              }}
            >
              <Typography.Text strong>
                {selectedIds.length} selected
                {hiddenSelectedCount > 0 && ` (${hiddenSelectedCount} hidden by filters)`}
              </Typography.Text>

              <Select
                placeholder="Select a version"
                style={{ width: 220 }}
                data-testid="set-version-select"
                disabled={selectedIds.length === 0}
                value={targetVersionId ?? undefined}
                onChange={setTargetVersionId}
                options={versions.map((v) => ({
                  label: `Version ${v.version}${v.isActive ? ' (active)' : ''}`,
                  value: v.id,
                }))}
              />
              <Popconfirm
                title={`Set core version for ${selectedIds.length} compan${selectedIds.length === 1 ? 'y' : 'ies'}?`}
                description={
                  <span style={{ display: 'block', maxWidth: 300 }}>
                    Their captures start using it within about 5 minutes. Custom rules are not
                    affected.
                  </span>
                }
                onConfirm={handleSetVersion}
              >
                <Button
                  disabled={!targetVersionId || selectedIds.length === 0}
                  loading={settingVersion}
                >
                  Set version
                </Button>
              </Popconfirm>

              {/* `applyTemplate` is semantically a `PromptTemplate` write, but these
                  controls stay inside this `manage CorePrompt` gate rather than a
                  separate `manage PromptTemplate` check — intentional for now: under
                  the current ability model (`manage all` / `read all`), the two
                  subjects are equivalent, and the whole bulk-action bar is treated as
                  one rollout-management gate. Latent seam if roles ever diverge
                  (a CorePrompt-only or PromptTemplate-only super admin) — split the
                  gate then, not before. */}
              <Select
                placeholder="Select a template"
                style={{ width: 220 }}
                data-testid="apply-template-select"
                disabled={selectedIds.length === 0}
                loading={templatesQuery?.isLoading}
                value={templateId ?? undefined}
                onChange={setTemplateId}
                options={templateOptions}
              />
              {/* Apply is an unconditional overwrite of `append_body` (no
                  IS NULL guard server-side), so it needs at least the same
                  confirmation as Set version — which never touches appends. */}
              <Popconfirm
                title={`Apply template to ${selectedIds.length} ${selectedIds.length === 1 ? 'company' : 'companies'}?`}
                description={
                  overwriteCount > 0
                    ? `${overwriteCount} of them already ${overwriteCount === 1 ? 'has' : 'have'} custom rules, which will be replaced.`
                    : undefined
                }
                okText={overwriteCount > 0 ? 'Replace' : 'Apply'}
                onConfirm={handleApplyTemplate}
              >
                <Button disabled={!templateId || selectedIds.length === 0} loading={applyingTemplate}>
                  Apply template
                </Button>
              </Popconfirm>
            </div>
          ) : null
        }
      </Can>

      {loading ? (
        <Spin />
      ) : (
        <Table<CompanyPromptGridRow>
          columns={columns}
          dataSource={filteredRows}
          rowKey="companyId"
          pagination={false}
          scroll={{ x: 'max-content' }}
          rowSelection={{
            type: 'checkbox',
            hideSelectAll: true,
            // Without this antd prunes selected keys whose rows aren't in the
            // current (filtered) dataSource on every selection change — so
            // search-for-A, check A, search-for-B, check B silently drops A and
            // the bulk action runs against B only. Mirrors BatchTable /
            // SalesforceExportDialog, which set it for the same reason.
            preserveSelectedRowKeys: true,
            selectedRowKeys: selectedRows.map((r) => r.companyId),
            onChange: (_keys, selected) => setSelectedRows(selected),
          }}
        />
      )}
    </>
  );
}
